import { useEffect, useState } from "react";
import { Users, MessageSquarePlus, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FB_TYPES = ["Strength", "Suggestion", "Question", "Next Step"];

export default function CoachDashboard() {
  const { lang } = useLang();
  const [teachers, setTeachers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activities, setActivities] = useState([]);
  const [fbType, setFbType] = useState("Strength");
  const [fbText, setFbText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/coach/teachers").then(({ data }) => setTeachers(data)).catch(() => {}); }, []);

  const openTeacher = async (tch) => {
    setSelected(tch); setActivities([]); setFbText("");
    try { const { data } = await api.get(`/coach/teacher/${tch.id}/activities`); setActivities(data); } catch {}
  };

  const sendFeedback = async () => {
    if (!fbText.trim()) return;
    setLoading(true);
    try {
      await api.post("/coach/feedback", { teacher_id: selected.id, feedback_type: fbType, text: fbText });
      toast.success(lang === "es" ? "Comentario enviado." : "Feedback sent.");
      setFbText("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-2xl bg-primary/15 flex items-center justify-center"><Users className="h-6 w-6 text-primary" /></span>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">{lang === "es" ? "Panel del Entrenador" : "Coach Dashboard"}</h1>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl">{lang === "es" ? "Maestros asignados" : "Assigned Teachers"}</h2>
            {teachers.length === 0 && <p className="text-muted-foreground">{lang === "es" ? "No hay maestros todavía." : "No teachers yet."}</p>}
            <div className="space-y-2">
              {teachers.map((t) => (
                <button key={t.id} onClick={() => openTeacher(t)} data-testid={`teacher-${t.id}`}
                  className={`w-full text-left rounded-xl border p-4 transition-colors ${selected?.id === t.id ? "border-primary bg-muted" : "border-border hover:border-primary/40"}`}>
                  <p className="font-semibold">{t.name}</p>
                  <p className="text-sm text-muted-foreground">{t.email} · {t.activity_count} {lang === "es" ? "actividades" : "activities"} · {t.plan_count} {lang === "es" ? "planes" : "plans"}</p>
                </button>
              ))}
            </div>
          </section>

          <section className="space-y-4">
            {!selected ? (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
                {lang === "es" ? "Selecciona un maestro para ver su trabajo y dejar comentarios." : "Select a teacher to review their work and leave feedback."}
              </div>
            ) : (
              <>
                <h2 className="font-heading font-bold text-xl">{selected.name}</h2>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {activities.length === 0 ? <p className="text-muted-foreground text-sm">{lang === "es" ? "Sin actividades." : "No activities."}</p> :
                    activities.map((a) => (
                      <div key={a.id} className="rounded-xl border border-border p-3 flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary shrink-0" />
                        <span className="text-sm truncate">{a.title}</span>
                      </div>
                    ))}
                </div>
                <div className="rounded-2xl border border-border p-4 space-y-3">
                  <div className="flex items-center gap-2"><MessageSquarePlus className="h-4 w-4 text-secondary" /><span className="font-heading font-bold">{lang === "es" ? "Dejar comentario" : "Leave feedback"}</span></div>
                  <div className="space-y-2">
                    <Label>{lang === "es" ? "Tipo" : "Type"}</Label>
                    <Select value={fbType} onValueChange={setFbType}>
                      <SelectTrigger className="h-11 rounded-xl" data-testid="fb-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{FB_TYPES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Textarea rows={3} value={fbText} onChange={(e) => setFbText(e.target.value)} className="rounded-xl resize-none" placeholder={lang === "es" ? "Tu comentario de apoyo..." : "Your supportive note..."} data-testid="fb-text" />
                  <Button onClick={sendFeedback} disabled={loading} className="rounded-full bg-secondary hover:bg-secondary/90" data-testid="fb-send">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : lang === "es" ? "Enviar" : "Send"}
                  </Button>
                </div>
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
